import React from 'react';

import { Button, Modal as ModalAntD, Tag } from 'antd';
import moment from 'moment';

import {
  ReconciliationDetail,
  RECONCILIATION_STATUS_META,
} from '../../../hooks/reconciliation';

const Modal: any = ModalAntD;

interface Props {
  visible: boolean;
  detail: ReconciliationDetail | null;
  onClose: () => void;
  onReprocess?: () => void;
  onManual?: () => void;
  isReprocessable?: boolean;
  isReprocessing?: boolean;
}

const translatePaymentType = (pt: string | null): string => {
  if (!pt) return '—';
  if (pt === 'PAGBANK_CARD') return 'Cartão PagBank';
  if (pt === 'PAGBANK_PIX') return 'PIX PagBank';
  if (pt === 'WALLET') return 'Carteira';
  return pt;
};

const fmtMoney = (v: number | string | null): string => {
  if (v === null || v === undefined) return '—';
  const n = typeof v === 'string' ? parseFloat(v) : v;
  if (!Number.isFinite(n)) return '—';
  return `R$ ${n.toFixed(2).replace('.', ',')}`;
};

const fmtPct = (v: string | null): string => {
  if (v === null || v === undefined) return '—';
  const n = parseFloat(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(0)}%`;
};

const fmtDateTime = (iso: string | null): string => {
  if (!iso) return '—';
  return moment(iso).format('DD/MM/YYYY HH:mm');
};

/**
 * MV2-1909: modal "Detalhe da conciliação" (mockup 5).
 * Mostra Cliente, Forma de pagamento, Datas, Conferência de valor e Histórico de
 * verificações. Botões Reprocessar + Conciliar manualmente conforme elegibilidade.
 */
const ReconciliationDetailModal: React.FC<Props> = ({
  visible,
  detail,
  onClose,
  onReprocess,
  onManual,
  isReprocessable,
  isReprocessing,
}) => {
  if (!detail) return null;

  const effective = detail.effective_status || detail.auto_status;
  const meta = effective ? RECONCILIATION_STATUS_META[effective] : null;
  const isManual = detail.effective_status === 'RECONCILED_MANUAL';
  const isReconciledAuto = detail.auto_status === 'RECONCILED' && !isManual;

  const expected = detail.expected_amount;
  const found = detail.found_amount;
  const diff = expected != null && found != null
    ? Math.abs(parseFloat(expected) - parseFloat(found))
    : null;

  // Pros botões: mostrar quando status não é RECONCILED e não manual (igual ao modal pós-upload)
  const showActions = !isManual && !isReconciledAuto;

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      width={420}
      destroyOnClose
      title={null}
      footer={null}
      bodyStyle={{ padding: 24 }}
    >
      <div style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.5px', color: 'rgba(0,0,0,.45)' }}>
        Detalhe da conciliação
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 4 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600, margin: 0 }}>Recarga {detail.transaction_pk}</h2>
        {meta && <Tag color={meta.color}>{meta.label}</Tag>}
      </div>

      <Grid>
        <Field label='Cliente' value={detail.customer_name || '—'} />
        <Field label='Forma de pagamento' value={translatePaymentType(detail.payment_type)} />
      </Grid>
      <Grid>
        <Field label='Data da recarga' value={fmtDateTime(detail.start_timestamp)} />
        <Field label='Data de pagamento' value={fmtDateTime(detail.paid_timestamp)} />
      </Grid>

      <SectionTitle>Conferência de valor</SectionTitle>
      <Box>
        <RowLine label='Valor bruto interno' value={fmtMoney(detail.value_brl)} />
        <RowLine label='% split do cliente' value={fmtPct(detail.split_percentage)} />
        <RowLine label='Valor esperado' value={fmtMoney(detail.expected_amount)} />
        <RowLine
          label='Valor recebido no PagBank'
          value={fmtMoney(detail.found_amount)}
          valueColor={diff != null && diff > 0 ? '#fa541c' : undefined}
        />
        {detail.refunded_brl != null && detail.refunded_brl > 0 && (
          <RowLine label='Valor estornado' value={fmtMoney(detail.refunded_brl)} valueColor='#faad14' />
        )}
        {diff != null && (
          <RowLine
            label={<strong>Diferença</strong>}
            value={<strong style={{ color: diff > 0 ? '#fa541c' : 'rgba(0,0,0,.45)' }}>{fmtMoney(diff)}</strong>}
          />
        )}
      </Box>

      <SectionTitle>Histórico</SectionTitle>
      <Timeline>
        <TimelineItem color='#faad14'>
          <strong>Verificado automaticamente</strong>
          {detail.auto_reconciled_at && (
            <div style={{ color: 'rgba(0,0,0,.65)', fontSize: 13 }}>
              {fmtDateTime(detail.auto_reconciled_at)}
              {detail.auto_status === 'RECONCILED' && ' · encontrado e valor confere.'}
              {detail.auto_status === 'DIVERGENCE' && ' · encontrado, mas valor não confere.'}
              {detail.auto_status === 'UNRECONCILED' && ' · não encontrado nos arquivos importados.'}
              {detail.auto_status === 'AMBIGUOUS' && ' · múltiplos matches ambíguos.'}
            </div>
          )}
        </TimelineItem>
        <TimelineItem color={isManual ? '#52c41a' : 'rgba(0,0,0,.25)'}>
          {isManual ? (
            <>
              <strong style={{ color: '#1890ff' }}>
                Conciliado manualmente
                {detail.manual_by_user_name && ` — ${detail.manual_by_user_name}`}
              </strong>
              {detail.manual_reason && (
                <div style={{ color: 'rgba(0,0,0,.65)', fontSize: 13, fontStyle: 'italic' }}>
                  &quot;{detail.manual_reason}&quot;
                </div>
              )}
              {detail.manual_at && (
                <div style={{ color: 'rgba(0,0,0,.45)', fontSize: 12 }}>
                  {fmtDateTime(detail.manual_at)}
                </div>
              )}
            </>
          ) : (
            <>
              <strong>Aguardando ação</strong>
              <div style={{ color: 'rgba(0,0,0,.45)', fontSize: 13 }}>
                Nenhuma conciliação manual registrada ainda
              </div>
            </>
          )}
        </TimelineItem>
      </Timeline>

      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 24 }}>
        <Button onClick={onClose}>Fechar</Button>
        {showActions && (
          <div style={{ display: 'flex', gap: 8 }}>
            {isReprocessable && onReprocess && (
              <Button loading={isReprocessing} onClick={onReprocess}>
                Reprocessar
              </Button>
            )}
            {onManual && (
              <Button type='primary' onClick={onManual}>
                Conciliar manualmente
              </Button>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
};

const Grid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 16 }}>
    {children}
  </div>
);

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ textTransform: 'uppercase', fontSize: 10, letterSpacing: '0.5px', color: 'rgba(0,0,0,.45)' }}>
      {label}
    </div>
    <div style={{ marginTop: 2, color: 'rgba(0,0,0,.85)' }}>{value}</div>
  </div>
);

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.5px', color: 'rgba(0,0,0,.45)', marginTop: 20 }}>
    {children}
  </div>
);

const Box: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ background: '#fafafa', borderRadius: 4, padding: 12, marginTop: 8 }}>{children}</div>
);

const RowLine: React.FC<{ label: React.ReactNode; value: React.ReactNode; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px dashed rgba(0,0,0,.08)' }}>
    <span style={{ color: 'rgba(0,0,0,.65)' }}>{label}</span>
    <span style={{ color: valueColor || 'rgba(0,0,0,.85)' }}>{value}</span>
  </div>
);

const Timeline: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div style={{ marginTop: 8 }}>{children}</div>
);

const TimelineItem: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
    <div style={{
      width: 10, height: 10, borderRadius: '50%',
      background: color, marginTop: 6, flexShrink: 0,
    }} />
    <div style={{ flex: 1 }}>{children}</div>
  </div>
);

export default ReconciliationDetailModal;
